import { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to detect if the app is running in embedded iframe mode
 * (e.g., Chart Squad integration) and provides utilities for parent window communication
 * 
 * @returns {Object} { isEmbedded, sendMessageToParent, canSendMessage }
 */
const useEmbedded = () => {
    const location = useLocation();
    
    const isEmbedded = useMemo(() => {
        return location.pathname.startsWith('/embed/');
    }, [location.pathname]);
    
    /**
     * Check if we can send messages to parent window
     * @returns {boolean} true if parent window exists and is different from self
     */
    const canSendMessage = useMemo(() => {
        if (!isEmbedded) return false;
        return window.parent && window.parent !== window.self;
    }, [isEmbedded]);
    
    /**
     * Send a message to the parent window (if embedded)
     * @param {Object} message - Message object to send
     * @param {string} targetOrigin - Target origin for postMessage (default: '*')
     */
    const sendMessageToParent = useCallback((message, targetOrigin = '*') => {
        if (canSendMessage) {
            window.parent.postMessage(message, targetOrigin);
        }
    }, [canSendMessage]);
    
    return {
        isEmbedded,
        sendMessageToParent,
        canSendMessage
    };
};

export default useEmbedded;
